import { Permission, Role } from '@campaign-portal/namespace/common/user';
import { RoleType } from '@campaign-portal/namespace/common/enums';

export enum AP_PERMISSIONS {
	API_CONNECTION_E = 'api_connection_edit',
	PORTALS_R = 'portals_read',
	PORTALS_E = 'portals_edit',
	PARTNERS_E = 'partners_edit',
	SUBSCR_R = 'subscriptions_read',
	SUBSCR_E = 'subscriptions_edit',
	V_SUBSCR_R = 'vendor_subscriptions_read',
	V_SUBSCR_E = 'vendor_subscriptions_edit',
	ENDPOINTS_R = 'endpoints_read',
	ENDPOINTS_E = 'endpoints_edit',
	PAYMENTS_R = 'payments_read',
	TRANSACTIONS_R = 'admin_transaction_history_read',
	PAYMENTS_E = 'payments_edit',
	SENDERS_R = 'sender_ids_read',
	SENDERS_E = 'sender_ids_edit',
	STATS_R = 'statistics_read',
	URL_SHORTENER_E = 'url_shortener_edit',
	USERS_R = 'users_read',
	USERS_E = 'users_edit',
	PAYMENTS_SYSTEMS_E = 'payment_systems_edit',
	REF_BOOK_E = 'ref_book_edit',
	CC_E = 'contract_companies_edit',
	HLR_E = 'hlr_edit',
	CUSTOM_FORMS_E = 'custom_forms_edit',
	GENERAL_E = 'general_edit',
	PRE_MODERATE_E = 'pre_moderate_edit',
	DASHBOARD_CHARTS_R = 'dashboard_charts_read',
	VIEW_MANAGER_STATISTICS = 'view_manager_statistics',
	MANAGE_NOTIFICATION = 'manage_mail_settings',
}

export enum AP_RESELLER_PERMISSIONS {
	API_CONNECTION_E = 'api_connection_edit',
	PORTALS_R = 'portals_read',
	PORTALS_E = 'portals_edit',
	PARTNERS_E = 'partners_edit',
	SUBSCR_R = 'subscriptions_read',
	SUBSCR_E = 'subscriptions_edit',
	V_SUBSCR_R = 'vendor_subscriptions_read',
	PAYMENTS_R = 'payments_read',
	TRANSACTIONS_R = 'admin_transaction_history_read',
	PAYMENTS_E = 'payments_edit',
	SENDERS_R = 'sender_ids_read',
	SENDERS_E = 'sender_ids_edit',
	STATS_R = 'statistics_read',
	USERS_R = 'users_read',
	USERS_E = 'users_edit',
	PAYMENTS_SYSTEMS_E = 'payment_systems_edit',
	CC_E = 'contract_companies_edit',
	CUSTOM_FORMS_E = 'custom_forms_edit',
	PRE_MODERATE_E = 'pre_moderate_edit',
	VIEW_MANAGER_STATISTICS = 'view_manager_statistics',
	DASHBOARD_CHARTS_R = 'dashboard_charts_read',
	URL_SHORTENER_E = 'url_shortener_edit',
	MANAGE_NOTIFICATION = 'manage_mail_settings',
}

export enum CP_PERMISSIONS {
	API_CONNECTION_E = 'api_connection_edit',
	CONTACTS_R = 'contacts_read',
	CONTACTS_E = 'contacts_edit',
	CAMPAIGNS_R = 'campaigns_read',
	CAMPAIGNS_E = 'campaigns_edit',
	MESSAGE_TEMPLATES_R = 'message_templates_read',
	MESSAGE_TEMPLATES_E = 'message_templates_edit',
	SENDERS_R = 'sender_ids_read',
	SENDERS_E = 'sender_ids_edit',
	STATS_R = 'statistics_read',
	SUBSCR_R = 'subscriptions_read',
	SUBSCR_E = 'subscriptions_edit',
	TRANSACTIONS_R = 'transaction_history_read',
	URL_SHORTENER_E = 'url_shortener_edit',
	DASHBOARD_CHARTS_R = 'dashboard_charts_read',
}

export enum CP_RESELLER_PERMISSIONS {
	API_CONNECTION_E = 'api_connection_edit',
	CONTACTS_R = 'contacts_read',
	CONTACTS_E = 'contacts_edit',
	CAMPAIGNS_R = 'campaigns_read',
	CAMPAIGNS_E = 'campaigns_edit',
	MESSAGE_TEMPLATES_R = 'message_templates_read',
	MESSAGE_TEMPLATES_E = 'message_templates_edit',
	SENDERS_R = 'sender_ids_read',
	SENDERS_E = 'sender_ids_edit',
	STATS_R = 'statistics_read',
	SUBSCR_R = 'subscriptions_read',
	SUBSCR_E = 'subscriptions_edit',
	TRANSACTIONS_R = 'transaction_history_read',
	DASHBOARD_CHARTS_R = 'dashboard_charts_read',
	URL_SHORTENER_E = 'url_shortener_edit',
}

const CAMPAIGN_USER: Role = {
	type: RoleType.CAMPAIGN_USER,
	permissions: Object.values(CP_PERMISSIONS),
};

const ADMIN: Role = {
	type: RoleType.ADMIN,
	permissions: Object.values(AP_PERMISSIONS),
};

const CLIENT_MANAGER: Role = {
	type: RoleType.CLIENT_MANAGER,
	permissions: [
		AP_PERMISSIONS.PARTNERS_E,
		AP_PERMISSIONS.SUBSCR_R,
		AP_PERMISSIONS.PAYMENTS_R,
		AP_PERMISSIONS.TRANSACTIONS_R,
		AP_PERMISSIONS.SENDERS_R,
		AP_PERMISSIONS.STATS_R,
		AP_PERMISSIONS.USERS_R,
		AP_PERMISSIONS.USERS_E,
		AP_PERMISSIONS.VIEW_MANAGER_STATISTICS,
	],
};
const NOC: Role = {
	type: RoleType.NOC,
	permissions: [
		AP_PERMISSIONS.PORTALS_R,
		AP_PERMISSIONS.PORTALS_E,
		AP_PERMISSIONS.ENDPOINTS_R,
		AP_PERMISSIONS.ENDPOINTS_E,
		AP_PERMISSIONS.PAYMENTS_SYSTEMS_E,
		AP_PERMISSIONS.REF_BOOK_E,
		AP_PERMISSIONS.USERS_R,
		AP_PERMISSIONS.USERS_E,
		AP_PERMISSIONS.VIEW_MANAGER_STATISTICS,
	],
};
const RATE_MANAGER: Role = {
	type: RoleType.RATE_MANAGER,
	permissions: [
		AP_PERMISSIONS.SUBSCR_R,
		AP_PERMISSIONS.SUBSCR_E,
		AP_PERMISSIONS.V_SUBSCR_R,
		AP_PERMISSIONS.V_SUBSCR_E,
		AP_PERMISSIONS.ENDPOINTS_R,
		AP_PERMISSIONS.ENDPOINTS_E,
		AP_PERMISSIONS.API_CONNECTION_E,
	],
};
const FINANCE_MANAGER: Role = {
	type: RoleType.FINANCE_MANAGER,
	permissions: [
		AP_PERMISSIONS.PAYMENTS_R,
		AP_PERMISSIONS.TRANSACTIONS_R,
		AP_PERMISSIONS.PAYMENTS_E,
		AP_PERMISSIONS.PAYMENTS_SYSTEMS_E,
	],
};

export const ROLES = {
	ADMIN,
	CAMPAIGN_USER,
	CLIENT_MANAGER,
	NOC,
	RATE_MANAGER,
	FINANCE_MANAGER,
};

const AP_COMMON_PERMISSION_GROUPS = {
	partners: [AP_PERMISSIONS.PARTNERS_E],
	rates: [AP_PERMISSIONS.SUBSCR_R, AP_PERMISSIONS.SUBSCR_E],
	finance: [
		AP_PERMISSIONS.PAYMENTS_SYSTEMS_E,
		AP_PERMISSIONS.PAYMENTS_R,
		AP_PERMISSIONS.PAYMENTS_E,
		AP_PERMISSIONS.TRANSACTIONS_R,
	],
	statistics: [AP_PERMISSIONS.STATS_R, AP_PERMISSIONS.VIEW_MANAGER_STATISTICS, AP_PERMISSIONS.DASHBOARD_CHARTS_R],
};

export const AP_PERMISSION_GROUPS = {
	...AP_COMMON_PERMISSION_GROUPS,
	administration: [
		AP_PERMISSIONS.CC_E,
		AP_PERMISSIONS.REF_BOOK_E,
		AP_PERMISSIONS.HLR_E,
		AP_PERMISSIONS.PORTALS_R,
		AP_PERMISSIONS.PORTALS_E,
		AP_PERMISSIONS.USERS_R,
		AP_PERMISSIONS.USERS_E,
		AP_PERMISSIONS.SENDERS_R,
		AP_PERMISSIONS.SENDERS_E,
		AP_PERMISSIONS.CUSTOM_FORMS_E,
		AP_PERMISSIONS.GENERAL_E,
		AP_PERMISSIONS.PRE_MODERATE_E,
		AP_PERMISSIONS.API_CONNECTION_E,
		AP_PERMISSIONS.URL_SHORTENER_E,
		AP_PERMISSIONS.MANAGE_NOTIFICATION,
	],
	routing: [
		AP_PERMISSIONS.V_SUBSCR_R,
		AP_PERMISSIONS.V_SUBSCR_E,
		AP_PERMISSIONS.ENDPOINTS_R,
		AP_PERMISSIONS.ENDPOINTS_E,
	],
};

export const AP_RESELLER_PERMISSION_GROUPS = {
	...AP_COMMON_PERMISSION_GROUPS,
	administration: [
		AP_PERMISSIONS.CC_E,
		AP_PERMISSIONS.PORTALS_R,
		AP_PERMISSIONS.PORTALS_E,
		AP_PERMISSIONS.USERS_R,
		AP_PERMISSIONS.USERS_E,
		AP_PERMISSIONS.SENDERS_R,
		AP_PERMISSIONS.SENDERS_E,
		AP_PERMISSIONS.CUSTOM_FORMS_E,
		AP_PERMISSIONS.PRE_MODERATE_E,
		AP_PERMISSIONS.API_CONNECTION_E,
		AP_PERMISSIONS.URL_SHORTENER_E,
		AP_PERMISSIONS.MANAGE_NOTIFICATION,
	],
	routing: [AP_PERMISSIONS.V_SUBSCR_R],
};

const CP_COMMON_PERMISSION_GROUPS = {
	contacts: [CP_PERMISSIONS.CONTACTS_R, CP_PERMISSIONS.CONTACTS_E],
	campaign: [
		CP_PERMISSIONS.CAMPAIGNS_R,
		CP_PERMISSIONS.CAMPAIGNS_E,
		CP_PERMISSIONS.MESSAGE_TEMPLATES_R,
		CP_PERMISSIONS.MESSAGE_TEMPLATES_E,
		CP_PERMISSIONS.SENDERS_R,
		CP_PERMISSIONS.SENDERS_E,
	],
	statistics: [CP_PERMISSIONS.STATS_R, CP_PERMISSIONS.DASHBOARD_CHARTS_R, CP_PERMISSIONS.URL_SHORTENER_E],
	billing: [CP_PERMISSIONS.SUBSCR_R, CP_PERMISSIONS.SUBSCR_E, CP_PERMISSIONS.TRANSACTIONS_R],
	administration: [CP_PERMISSIONS.API_CONNECTION_E],
};

export const CP_PERMISSION_GROUPS = {
	...CP_COMMON_PERMISSION_GROUPS,
};

export const CP_RESELLER_PERMISSION_GROUPS = {
	...CP_COMMON_PERMISSION_GROUPS,
};

export type PermissionGroup = Record<string, Permission[]>;

export type PermissionGroupKey = keyof (typeof AP_PERMISSION_GROUPS &
	typeof AP_RESELLER_PERMISSION_GROUPS &
	typeof CP_PERMISSION_GROUPS &
	typeof CP_RESELLER_PERMISSION_GROUPS);
